import React, { Fragment, useState, useCallback } from 'react';
import {Snackbar, TextField} from '@material-ui/core';
import ConfirmDialog, { ConfirmDialogProps } from '../ConfirmDialog';
import AddSettingDialog from './AddSettingDialog';
import SettingsTable, { Column } from './SettingsTable';
import SettingsComponent from './SettingsComponent';
import NumberFormat from 'react-number-format';
import NumericTextField from '../NumericTextField';
import CustomSnackbarContent from "../CustomSnackbarContent";
import { deleteAllowed, getAll, getOne, post, remove } from "../../HTTPClients/RecipeApp/sieve-tests/sieve-sizes";

const columns: Column[] = [
  {
    name: 'code',
    title: 'Code'
  },
  {
    name: 'size',
    title: 'Afmeting (mm)',
    getValue: (value: number) => <NumberFormat displayType="text" value={value} thousandSeparator="." decimalSeparator="," />
  }
];

const SieveSizes: React.FC<{ PrevComponent: React.FC<{ SearchElement?: JSX.Element }> }> = ({ PrevComponent }) => {
  const [dialogProps, confirmDelete] = useState({ open: false } as Omit<ConfirmDialogProps, 'title' | 'content'>);
  const [filter, setFilter] = useState('');
  const [snackbar, setSnackbar] = useState(undefined as { message: string, variant: 'success' | 'warning' | 'error' | 'info' } | undefined);

  const getRows = useCallback(async () => {
    let response=await getAll();
    return response.data.data;
  },[]);

  const getRow = useCallback(async (sieveSizeId: string) => {
    let response=await getOne(Number(sieveSizeId));
    return response.data.data;
  },[]);

  const handleSave = useCallback(async (sieveSize: any, sieveSizes: any[] | undefined, saveCallback: (sieveSizes?: any[]) => void) => {
    if (sieveSizes && sieveSize.id) {
      post(sieveSize).then(
          function(response) {
            const index = sieveSizes.findIndex(f => f.id === sieveSize.id);
            index >= 0 && (sieveSizes[index] = sieveSize);
            saveCallback(sieveSizes);
          }
      ).catch(
          function (error) {
            if(error.response===undefined)
              console.log(error);
            else
            if(error.response.data==='not_unique') notUniqueSnackbar();
          }
      )
    } else if (sieveSizes) {
      post(sieveSize).then(
          function(response) {
            sieveSizes.push({ ...sieveSize, id: response.data.data.id });
            saveCallback(sieveSizes);
          }
      ).catch(
          function (error) {
            if(error.response===undefined)
              console.log(error);
            else
            if(error.response.data==='not_unique') notUniqueSnackbar();
          }
      )
    }
  },[]);

  function notUniqueSnackbar() {
    setSnackbar({
      variant: 'warning',
      message: 'Deze zeefmaat bestaat al'
    })
  }

  const handleDelete = useCallback((sieveSize: any, deleteCallback: (sieveSize: any) => void) => {
    deleteAllowed(sieveSize.id).then(
        function(response) {
          if(response.data.allowed) {
            confirmDelete({
              open: true,
              onCancel: () => confirmDelete({ open: false }),
              onConfirm: async () => {
                remove(sieveSize.id).then(
                    function(response) {
                      deleteCallback(sieveSize);
                    }
                ).finally(
                    function() { confirmDelete({ open: false }); }
                )
              }
            });
          } else {
            noDeleteSnackbar();
            return;
          }
        }
    )
  },[]);

  function noDeleteSnackbar() {
    setSnackbar({
      variant: 'warning',
      message: 'Deze zeefmaat mag niet verwijderd worden'
    })
  }

  const SearchElement = <TextField placeholder="Zeefmaat zoeken..." value={filter} onChange={e => setFilter(e.target.value)} variant="outlined" margin="dense" />;

  return (
    <SettingsComponent
      PrevComponent={PrevComponent}
      SearchElement={SearchElement}
      path="/settings/sieve_sizes"
      getRows={getRows}
      getRow={getRow}
    >{({ rows, row, addDialogOpen, onEdit, onSave, onChange, onDelete, onCancelAdd }) => (
      <Fragment>
        {rows && <SettingsTable
          columns={columns}
          rows={rows.filter(f => f.code.toLowerCase().indexOf(filter.toLowerCase()) >= 0).sort((a, b) => b.size - a.size)}
          onEdit={(sieveSize: any) => sieveSize.id && onEdit(sieveSize.id)}
          onDelete={(sieveSize: any) => handleDelete(sieveSize, onDelete)}
        />}
        {!row ? null : (
          <AddSettingDialog
            title={row.id ? 'Zeefmaat bewerken' : 'Zeefmaat toevoegen'}
            open={addDialogOpen}
            onClose={() => onCancelAdd()}
            onSave={() => handleSave(row, rows, onSave)}
          >
            <TextField
              label="Zeefmaat code"
              value={row.code}
              onChange={e => onChange({ ...row, code: e.target.value })}
              variant="outlined"
              margin="normal"
              fullWidth={true}
            />
            <NumericTextField
              label="Zeefmaat afmeting"
              value={row.size}
              onChange={e => onChange({ ...row, size: e.target.value })}
              fullWidth={true}
            />
          </AddSettingDialog>
        )}
        <ConfirmDialog {...dialogProps} title="Zeefmaat verwijderen" content="Weet u zeker dat u deze zeefmaat wilt verwijderen?" />
        <Snackbar open={Boolean(snackbar)} onClose={() => setSnackbar(undefined)} autoHideDuration={6000}>
          <CustomSnackbarContent
              variant={snackbar ? snackbar.variant : undefined}
              message={snackbar ? snackbar.message : undefined}
          />
        </Snackbar>
      </Fragment>
    )}
    </SettingsComponent>
  )
}

export default SieveSizes;
